<template>
  <div class="history-page">
    <div v-if="loading" class="loading">
      <van-loading type="spinner" />
    </div>
    <div v-else class="content">
      <van-empty v-if="steps.length === 0" description="No history" />
      <div v-else class="timeline">
        <div v-for="(s, idx) in steps" :key="idx" class="step">
          <div class="rail">
            <div class="dot">
              <div class="dot-outer" :style="{ borderColor: getColor(idx, s.operationType) }"></div>
              <div
                class="dot-inner"
                :style="{
                  backgroundColor:
                    idx === 0 && currentTaskName ? '#777' : getColor(idx, s.operationType),
                }"
              ></div>
            </div>
            <div
              v-if="idx !== steps.length - 1"
              class="line"
              :style="{ backgroundColor: getColor(idx, s.operationType) }"
            ></div>
          </div>
          <div class="info">
            <div class="title" :style="{ color: idx === 0 ? '#777' : '#333' }">
              {{ s.title || '' }}
            </div>
            <template v-if="idx !== 0 || !currentTaskName">
              <div class="row">
                <span class="label">Gate Owner:</span
                ><span class="value">{{ s.gateOwner || '' }}</span>
              </div>
              <div class="row">
                <span class="label">Operation Time:</span
                ><span class="value">{{ s.operationTime || '' }}</span>
              </div>
              <div class="row">
                <span class="label">Operation Type:</span
                ><span class="value">{{ s.operationType || '' }}</span>
              </div>
              <div class="row">
                <span class="label">Client Type:</span
                ><span class="value">{{ s.sourceType || '' }}</span>
              </div>
              <div class="row">
                <span class="label">Comments:</span
                ><span class="value">{{ s.comments || '' }}</span>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { getStepflowList } from '@/api/history'

const props = defineProps<{ detailData: any }>()
const route = useRoute()

const loading = ref(false)
const steps = ref<any[]>([])

const batchId = computed(() => String(route.query.batchId || ''))
const clcDoorCode = computed(() => String(route.query.clcDoorCode || ''))
const taskId = computed(() => String(route.query.taskId || ''))
const currentTaskName = computed(() => String(route.query.currentTaskName || ''))
const processDefinitionKey = computed(() => String(props.detailData?.processDefinitionKey || ''))

const fetchHistory = async () => {
  loading.value = true
  try {
    const res = await getStepflowList({
      batchId: batchId.value,
      doorCode: clcDoorCode.value,
      workflow: processDefinitionKey.value,
      name: currentTaskName.value || null,
      taskId: taskId.value,
    })
    const list = Array.isArray((res as any)?.data?.item)
      ? (res as any).data.item.slice().reverse()
      : []
    steps.value = list
  } catch (e) {
  } finally {
    loading.value = false
  }
}

onMounted(fetchHistory)

const getColor = (idx: number, type: string | null) => {
  if (idx === 0 && currentTaskName.value) return '#777'
  const t = String(type || '')
    .trim()
    .toLowerCase()
  if (!t) return '#777'
  const greenOperationTypes = ['approve', 'submit', 'start process']
  if (t === 'reject') return '#ED4014'
  if (greenOperationTypes.includes(t)) return '#70B503'
  return '#F59B22'
}
</script>

<style scoped lang="scss">
.history-page {
  padding: 12px;
}

@media (orientation: landscape) {
  .history-page {
    zoom: 0.5;
  }
}

.loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.timeline {
  padding: 4px 8px;
}
.step {
  display: flex;
}
.rail {
  width: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.dot {
  width: 16px;
  height: 16px;
  position: relative;
  margin-top: 3px;
}
.dot-outer {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  border: 2px solid #70b503;
  background: transparent;
}
.dot-inner {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #70b503;
}
.line {
  width: 2px;
  flex: 1;
  background: #70b503;
  margin-top: 4px;
}
.info {
  flex: 1;
  padding-left: 5px;
  padding-bottom: 12px;
}
.title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
  border: none;
}
.row {
  font-size: 12px;
  line-height: 20px;
}
.label {
  color: #777;
  margin-right: 6px;
}
.value {
  color: #333;
}
</style>
